import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { useGetDocuments } from 'utils/DocumentsHooks';
import { showToast } from 'stores/ToastStore';
import { FilterChip } from 'components/design-system/molecules/FilterChip';
import { FilterValueEditor } from 'components/design-system/molecules/FilterValueEditor';
import { sluggify } from '../../utils/localization/utils';

export default function MultiSelectDocumentType({
  shipmentId,
  foreignKeys,
  filterDocumentTypes,
  setFilterDocumentTypes,
  excludedDocTypes,
  includedDocTypes,
  refetch,
  setRefetch,
}) {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const showGetDocumentFilterTypeErrorToast = (err) => {
    showToast(
      t('GET_DOCUMENT_FILTER_TYPE_ERROR'),
      t('GET_DOCUMENT_FILTER_TYPE_ERROR', { error: err }),
      'error',
    );
  };

  const resolvedForeignKeys = useMemo(
    () =>
      foreignKeys && foreignKeys.length > 0
        ? foreignKeys
        : [shipmentId].filter(Boolean),
    [foreignKeys, shipmentId],
  );

  const {
    data: documentsData,
    error: documentError,
    refetch: documentsRefetch,
  } = useGetDocuments({
    foreignKeys: resolvedForeignKeys,
    type: includedDocTypes,
    excludeTypes: excludedDocTypes,
    options: {
      limit: 1000,
      skip: 0,
      order: [{ field: 'createdAt', direction: 'desc' }],
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (refetch) {
      documentsRefetch();
      setRefetch(false);
    }
  }, [refetch]);

  useEffect(() => {
    if (documentError) {
      showGetDocumentFilterTypeErrorToast(documentError);
    }
  }, [documentError]);

  useEffect(() => {
    if (documentsData && documentsData.documents.length > 0) {
      const uniqueTypes = [
        ...new Set(documentsData.documents.map((d) => d.type)),
      ];
      const formattedTypes = uniqueTypes.map((type) => ({
        value: type,
        label: t(sluggify(type)),
      }));
      setTypes(formattedTypes);
    } else if (documentsData) {
      setTypes([]);
    }
  }, [documentsData]);

  const config = useMemo(
    () => ({
      id: 'document_type',
      label: titleize(t('DOCUMENT_TYPE')),
      kind: 'select-multi',
      options: types,
      initialValue: [],
    }),
    [t, types],
  );

  if (types.length === 0) {
    return null;
  }

  return (
    <FilterChip
      config={config}
      value={filterDocumentTypes}
      onRemove={() => setFilterDocumentTypes([])}
    >
      <FilterValueEditor
        config={config}
        value={filterDocumentTypes}
        onChange={(next) => setFilterDocumentTypes(next || [])}
      />
    </FilterChip>
  );
}
